"use client";

import Image from "next/image";
import {
  BadgeCheck,
  BriefcaseBusiness,
  Check,
  FileCode2,
  Globe,
  Lock,
  MapPin,
  Newspaper,
  TrendingUp,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { devSectionAttr } from "@/lib/dev/section-attr";

import type { GeoSubpageCopy } from "./geo-page-copy";

type Toolbox = GeoSubpageCopy["toolbox"];
type DemoCopy = Toolbox["demo"];

const FACT_ICONS: Record<string, LucideIcon> = {
  MapPin,
  BriefcaseBusiness,
  Users,
  Globe,
};

/** Example trend line for the measurement dashboard (labelled as an illustration). */
const TREND = [24, 29, 33, 41, 52, 62];

function Logo({ src, size = 18 }: { src: string; size?: number }) {
  return (
    <Image
      src={src}
      alt=""
      width={size}
      height={size}
      unoptimized
      loading="eager"
    />
  );
}

/** Knowledge-panel style card: who the business is, the same everywhere. */
function EntityDemo({ demo }: { demo: DemoCopy }) {
  const e = demo.entity;
  return (
    <div className="cc-geo-demo cc-geo-demo--entity">
      <article className="cc-geo-kp">
        <header>
          <span className="cc-geo-kp__mark">{demo.brandInitials}</span>
          <span>
            <strong>{demo.brand}</strong>
            <small>{e.category}</small>
          </span>
          <BadgeCheck
            className="cc-geo-kp__check"
            size={18}
            strokeWidth={2.2}
          />
        </header>
        <ul>
          {e.facts.map((fact, i) => {
            const Icon = FACT_ICONS[fact.icon] ?? Globe;
            return (
              <li key={fact.icon} style={{ ["--i" as string]: i }}>
                <Icon size={15} strokeWidth={2} />
                <span>{fact.label}</span>
                <b>{fact.value}</b>
              </li>
            );
          })}
        </ul>
        <footer>
          <span className="cc-geo-kp__same">
            {e.sameAs}
            <span>
              {e.profiles.map((src) => (
                <Logo key={src} src={src} size={16} />
              ))}
            </span>
          </span>
          <code>
            <FileCode2 size={13} strokeWidth={2} />
            {e.schema}
          </code>
        </footer>
      </article>
      <span className="cc-geo-demo__badge">
        <Check size={13} strokeWidth={3} />
        {e.verified}
      </span>
    </div>
  );
}

/** Real-looking sources that mention the business, flowing into an AI service. */
function SourcesDemo({ demo }: { demo: DemoCopy }) {
  const src = demo.sources;
  return (
    <div className="cc-geo-demo cc-geo-demo--sources">
      <ul className="cc-geo-src">
        {src.items.map((item, i) => (
          <li key={item.id} style={{ ["--i" as string]: i }}>
            <span className="cc-geo-src__logo">
              {item.logo ? (
                <Logo src={item.logo} size={20} />
              ) : (
                <Newspaper size={18} strokeWidth={2} />
              )}
            </span>
            <span className="cc-geo-src__body">
              <span className="cc-geo-src__top">
                <b>{item.name}</b>
                <em>{item.meta}</em>
              </span>
              <span className="cc-geo-src__text">{item.text}</span>
            </span>
            <mark>{src.mentions}</mark>
          </li>
        ))}
      </ul>
      <span className="cc-geo-src__ai">
        <Logo src="/assets/imgs/tools/chatgpt.svg" size={18} />
        {src.reading}
        <span className="cc-geo-src__dots">
          <i />
          <i />
          <i />
        </span>
      </span>
    </div>
  );
}

/** A real page in a browser window, with the answer-first paragraph cited by AI. */
function ContentDemo({ demo }: { demo: DemoCopy }) {
  const c = demo.content;
  return (
    <div className="cc-geo-demo cc-geo-demo--content">
      <div className="cc-geo-browser">
        <div className="cc-geo-browser__bar">
          <span className="cc-geo-browser__dots">
            <i />
            <i />
            <i />
          </span>
          <span className="cc-geo-browser__url">
            <Lock size={11} strokeWidth={2.4} />
            {c.url}
          </span>
        </div>
        <div className="cc-geo-browser__page">
          <h4>{c.h1}</h4>
          <p className="cc-geo-browser__answer">{c.answer}</p>
          <table>
            <tbody>
              {c.table.map((row) => (
                <tr key={row[0]}>
                  <th>{row[0]}</th>
                  <td>{row[1]}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <ul>
            {c.faq.map((q) => (
              <li key={q}>{q}</li>
            ))}
          </ul>
        </div>
      </div>
      <span className="cc-geo-browser__cite">
        <Logo src="/assets/imgs/tools/gemini.svg" size={16} />
        {c.cite}
        <b>1</b>
      </span>
    </div>
  );
}

/** robots.txt in an editor window: every AI crawler allowed, plus health checks. */
function TechDemo({ demo }: { demo: DemoCopy }) {
  const t = demo.tech;
  return (
    <div className="cc-geo-demo cc-geo-demo--tech">
      <div className="cc-geo-term">
        <div className="cc-geo-term__bar">
          <span className="cc-geo-browser__dots">
            <i />
            <i />
            <i />
          </span>
          <code>{t.file}</code>
          <em>{t.status}</em>
        </div>
        <div className="cc-geo-term__body">
          <span className="cc-geo-demo__scan" />
          {t.bots.map((bot, i) => (
            <div
              key={bot.name}
              className="cc-geo-term__row"
              style={{ ["--i" as string]: i }}
            >
              <Logo src={bot.logo} size={16} />
              <code>
                <span>User-agent:</span> {bot.name}
                <br />
                <span>{t.allow.split(":")[0]}:</span>
                {t.allow.split(":")[1]}
              </code>
              <em>
                <Check size={11} strokeWidth={3} />
                {demo.allowed}
              </em>
            </div>
          ))}
        </div>
      </div>
      <ul className="cc-geo-term__checks">
        {t.checks.map((check, i) => (
          <li key={check} style={{ ["--i" as string]: i }}>
            <Check size={13} strokeWidth={3} />
            {check}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** A small analytics dashboard: share of answers over time and per AI service. */
function MeasureDemo({ demo }: { demo: DemoCopy }) {
  const m = demo.measure;
  const max = 70;
  const pts = TREND.map(
    (v, i) => `${(i / (TREND.length - 1)) * 100},${100 - (v / max) * 100}`,
  ).join(" ");
  return (
    <div className="cc-geo-demo cc-geo-demo--measure">
      <div className="cc-geo-dash">
        <header>
          <span>
            <b>{m.title}</b>
            <small>{m.period}</small>
          </span>
          <em>
            <TrendingUp size={14} strokeWidth={2.4} />
            {m.trend} {m.trendLabel}
          </em>
        </header>
        <div className="cc-geo-dash__chart">
          <svg viewBox="0 0 100 100" preserveAspectRatio="none">
            <polygon
              points={`0,100 ${pts} 100,100`}
              className="cc-geo-dash__area"
            />
            <polyline
              points={pts}
              className="cc-geo-dash__line"
              pathLength={1}
            />
          </svg>
          <span className="cc-geo-dash__months">
            {m.months.map((month) => (
              <small key={month}>{month}</small>
            ))}
          </span>
        </div>
        <ul className="cc-geo-dash__engines">
          {m.engines.map((engine, i) => (
            <li
              key={engine.name}
              style={{
                ["--w" as string]: `${engine.value}%`,
                ["--i" as string]: i,
              }}
            >
              <Logo src={engine.logo} size={16} />
              <span>{engine.name}</span>
              <i>
                <b />
              </i>
              <strong>{engine.value} %</strong>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/** One live demo per building block; remounted on change so its entrance replays. */
function Demo({ id, demo }: { id: string; demo: DemoCopy }) {
  if (id === "entity") return <EntityDemo demo={demo} />;
  if (id === "sources") return <SourcesDemo demo={demo} />;
  if (id === "content") return <ContentDemo demo={demo} />;
  if (id === "tech") return <TechDemo demo={demo} />;
  return <MeasureDemo demo={demo} />;
}

/**
 * anime.js-style toolbox: the building blocks as a list on the left that
 * lights up while you scroll, and a pinned live demo on the right for the
 * active one (entity graph, sources, citable content, crawlers, measurement).
 */
export function CcGeoToolbox({ copy }: { copy: Toolbox }) {
  const [active, setActive] = useState(0);
  const refs = useRef<(HTMLLIElement | null)[]>([]);

  useEffect(() => {
    const items = refs.current.filter(Boolean) as HTMLLIElement[];
    if (!items.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting)
            setActive(Number((entry.target as HTMLElement).dataset.index));
        }
      },
      {
        rootMargin:
          window.innerWidth < 810 ? "-72% 0px -18% 0px" : "-45% 0px -45% 0px",
      },
    );
    items.forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, []);

  const current = copy.items[active];

  return (
    <section
      className="cc-section cc-geo-tools"
      id="sa-fungerar-geo"
      aria-labelledby="cc-geo-tools-title"
      data-cc-nav="light"
      {...devSectionAttr("geo-toolbox")}
    >
      <div className="cc-geo-tools__container">
        <header className="cc-geo-head">
          <p className="cc-geo-mono">{copy.eyebrow}</p>
          <h2 id="cc-geo-tools-title" className="cc-geo-title">
            {copy.title}
          </h2>
          <p className="cc-geo-intro">{copy.intro}</p>
        </header>

        <div className="cc-geo-tools__grid">
          <ol className="cc-geo-tools__list">
            {copy.items.map((item, i) => (
              <li
                key={item.id}
                ref={(node) => {
                  refs.current[i] = node;
                }}
                data-index={i}
                className="cc-geo-tools__item"
                data-active={i === active ? "true" : undefined}
              >
                <span className="cc-geo-mono">
                  {String(i + 1).padStart(2, "0")} · {item.label}
                </span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
                <ul>
                  {item.chips.map((chip) => (
                    <li key={chip}>{chip}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>

          <div className="cc-geo-tools__sticky">
            <figure className="cc-geo-tools__stage" aria-hidden="true">
              <span className="cc-geo-tools__labels">
                {copy.items.map((item, i) => (
                  <i key={item.id} data-on={i === active ? "true" : undefined}>
                    {item.label}
                  </i>
                ))}
              </span>
              <Demo key={current.id} id={current.id} demo={copy.demo} />
              <small className="cc-geo-tools__note">
                {copy.demo.exampleNote}
              </small>
            </figure>
          </div>
        </div>
      </div>
    </section>
  );
}
